import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, Users, Smartphone, Download, ArrowRight } from 'lucide-react';

const HowItWorksSection = () => {
  const steps = [
    {
      number: 1,
      icon: Upload,
      title: "Upload video after class",
      description: "Snap a video on your phone or upload from any device. Quick and simple.",
      details: "• Upload from phone or computer\n• Drag & drop or record directly\n• Works with any video format"
    },
    {
      number: 2,
      icon: Users,
      title: "Assign to class, group, or family",
      description: "Choose exactly who should see this video with our smart family controls.",
      details: "• Select specific families\n• Assign to entire classes\n• Create custom groups"
    },
    {
      number: 3,
      icon: Smartphone,
      title: "Parents get secure access",
      description: "Parents receive an invite-only link or log in via the HiveShare iOS app.",
      details: "• Instant notifications\n• iOS app for easy viewing\n• Web access available too"
    },
    {
      number: 4,
      icon: Download,
      title: "Stream, download, organize",
      description: "Videos are streamable, downloadable, and automatically organized by family.",
      details: "• HD streaming anywhere\n• Download for offline viewing\n• Automatically organized"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Four simple steps to share videos securely with families. 
            <span className="font-semibold text-blue-600"> Zero tech skills required.</span>
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-6xl mx-auto"
        >
          {/* Desktop Layout */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <motion.div key={step.number} variants={itemVariants} className="relative">
                  {/* Connection Line */}
                  {index < steps.length - 1 && (
                    <div className="absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-blue-200 to-blue-300 z-0">
                      <ArrowRight className="absolute -right-2 -top-2 h-4 w-4 text-blue-400" />
                    </div>
                  )}
                  
                  <Card className="relative z-10 h-full border-2 border-blue-100 hover:border-blue-200 transition-all duration-300 hover:shadow-lg">
                    <CardContent className="p-6 text-center h-full flex flex-col">
                      {/* Step Number */}
                      <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                        {step.number}
                      </div>
                      
                      {/* Icon */}
                      <div className="bg-blue-100 text-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <step.icon className="h-8 w-8" />
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-lg font-bold text-gray-900 mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-600 mb-4 flex-grow">
                        {step.description}
                      </p>
                      
                      {/* Details */}
                      <div className="text-sm text-gray-500 text-left">
                        {step.details.split('\n').map((detail, idx) => (
                          <div key={idx} className="mb-1">{detail}</div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden space-y-8">
            {steps.map((step, index) => (
              <motion.div key={step.number} variants={itemVariants} className="relative">
                <Card className="border-2 border-blue-100 hover:border-blue-200 transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-6">
                      {/* Step Number & Icon */}
                      <div className="flex-shrink-0">
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg mb-4">
                          {step.number}
                        </div>
                        <div className="bg-blue-100 text-blue-600 rounded-full w-16 h-16 flex items-center justify-center">
                          <step.icon className="h-8 w-8" />
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-3">
                          {step.title}
                        </h3>
                        <p className="text-gray-600 mb-4">
                          {step.description}
                        </p>
                        <div className="text-sm text-gray-500">
                          {step.details.split('\n').map((detail, idx) => (
                            <div key={idx} className="mb-1">{detail}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Connection Arrow for Mobile */}
                {index < steps.length - 1 && (
                  <div className="flex justify-center py-4">
                    <ArrowRight className="h-6 w-6 text-blue-400 rotate-90" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Time Saver Callout */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16 max-w-4xl mx-auto"
        >
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 md:p-12 text-center border border-green-200">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              ⏱️ From Class to Parent: Under 30 Seconds
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              What used to take hours of organizing, uploading, and emailing now happens in seconds.
              <span className="font-semibold text-green-700"> Your time is valuable.</span>
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-red-600 mb-2">5+ hours</div>
                <div className="text-sm text-gray-600">Old way: USB drives, email chains</div>
              </div>
              <div className="flex items-center justify-center">
                <ArrowRight className="h-8 w-8 text-gray-400" />
              </div>
              <div>
                <div className="text-3xl font-bold text-green-600 mb-2">&lt;30 sec</div>
                <div className="text-sm text-gray-600">HiveShare: Upload, tag, done</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;